import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useToast, useConfirm } from '@/components/ui';
import { snapshotsApi, Snapshot } from '@/lib/snapshots';
import { formatLocaleDate } from '@/lib/date';

function isExpired(s: Snapshot): boolean {
  return !!s.expiresAt && new Date(s.expiresAt).getTime() < Date.now();
}

export function SharedLinksPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const listQ = useQuery({
    queryKey: ['snapshots', wsId],
    queryFn: () => snapshotsApi.list(wsId),
    enabled: !!wsId,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => snapshotsApi.remove(wsId, id),
    onSuccess: () => {
      toast.success(t('shared_links.revoked'));
      qc.invalidateQueries({ queryKey: ['snapshots', wsId] });
    },
    onError: () => toast.error(t('common.delete_failed')),
  });

  const askRevoke = async (s: Snapshot) => {
    const ok = await confirm({
      title: t('shared_links.revoke_confirm_title'),
      description: t('shared_links.revoke_confirm_body'),
      confirmLabel: t('shared_links.revoke'),
      variant: 'danger',
    });
    if (ok) revoke.mutate(s.id);
  };

  async function copyLink(s: Snapshot) {
    try {
      await navigator.clipboard.writeText(snapshotsApi.publicUrl(s.id));
      toast.success(t('shared_links.copied'));
    } catch {
      // clipboard blocked — silent
    }
  }

  const items = listQ.data ?? [];
  const empty = !listQ.isLoading && items.length === 0;

  return (
    <AppShell titleKey="shared_links.title">
      <p className="mb-6 text-sm text-ink-600 max-w-2xl">{t('shared_links.intro')}</p>

      {listQ.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {empty && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500 max-w-3xl">
          <p className="text-sm">{t('shared_links.empty_title')}</p>
          <p className="text-xs mt-2">{t('shared_links.empty_sub')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('shared_links.col_label')}</th>
                <th className="py-2.5 px-3 font-medium">{t('shared_links.col_kind')}</th>
                <th className="py-2.5 px-3 font-medium">{t('shared_links.col_created')}</th>
                <th className="py-2.5 px-3 font-medium">{t('shared_links.col_expires')}</th>
                <th className="py-2.5 px-3 font-medium text-right" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {items.map((s) => {
                const expired = isExpired(s);
                return (
                  <tr key={s.id} className="border-b border-ink-100">
                    <td className="py-2.5 px-3">
                      <div className="font-medium text-ink-900 truncate max-w-xs">
                        {s.label || s.id.slice(0, 8)}
                      </div>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-[10px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 bg-ink-100 text-ink-600">
                        {s.kind}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 num text-ink-700">
                      {formatLocaleDate(s.createdAt, i18n.language)}
                    </td>
                    <td className="py-2.5 px-3 text-ink-700">
                      {expired ? (
                        <span className="text-[10px] uppercase tracking-wide font-semibold rounded-md px-1.5 py-0.5 bg-warn-50 text-warn-600">
                          {t('shared_links.expired')}
                        </span>
                      ) : s.expiresAt ? (
                        <span className="num">{formatLocaleDate(s.expiresAt, i18n.language)}</span>
                      ) : (
                        <span className="text-ink-400">{t('shared_links.no_expiry')}</span>
                      )}
                    </td>
                    <td className="py-2.5 px-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => copyLink(s)}
                          aria-label={t('shared_links.copy_link')}
                          title={t('shared_links.copy_link')}
                          className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        <a
                          href={snapshotsApi.publicUrl(s.id)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={t('shared_links.open')}
                          title={t('shared_links.open')}
                          className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => askRevoke(s)}
                          disabled={revoke.isPending}
                          aria-label={t('shared_links.revoke')}
                          title={t('shared_links.revoke')}
                          className="rounded-md p-1.5 text-warn-600 hover:bg-warn-500/10 disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
